import { Bot, CheckCircle, type LucideIcon, Pause, UserRound, X, XCircle } from 'lucide-react'
import React from 'react'
import { ThreeDots } from 'react-loading-icons'

import TooltipWrapper from 'components/reusable/TooltipWrapper'
import type { BrokersDependencyTaskSummary } from 'utils/web_api.schemas'

// Type for icon that can be either a Lucide icon or a custom component
type IconComponent = LucideIcon | React.ComponentType<any>

interface TaskDisplayMetadata {
  Icon: IconComponent
  iconBgColor: string
  iconColor: string
  iconBorderColor: string
  tooltipText: string
}

/**
 * Get display metadata for a task
 * Pure function that returns icon, colors (matching badge styles), and tooltip based on task state
 */
const getTaskDisplayMetadata = (
  task: BrokersDependencyTaskSummary
): TaskDisplayMetadata => {
  // Processing task - currently executing
  if (task.status === 'processing') {
    const ThreeDotsIcon = (props: any) => (
      <ThreeDots
        {...props}
        height={12}
        width={12}
        fill='#60a5fa' // blue-400
        stroke='#60a5fa'
      />
    )

    return {
      Icon: ThreeDotsIcon,
      iconBgColor: 'bg-blue-50',
      iconColor: 'text-blue-400',
      iconBorderColor: 'border-blue-300',
      tooltipText: 'Currently processing',
    }
  }

  // Completed task - successfully finished
  if (task.status === 'completed') {
    return {
      Icon: CheckCircle,
      iconBgColor: 'bg-green-50',
      iconColor: 'text-green-500',
      iconBorderColor: 'border-green-300',
      tooltipText: 'Completed',
    }
  }

  // Failed task - execution failed
  if (task.status === 'failed') {
    return {
      Icon: XCircle,
      iconBgColor: 'bg-red-50',
      iconColor: 'text-red-500',
      iconBorderColor: 'border-red-300',
      tooltipText: 'Failed',
    }
  }

  // Canceled task - was canceled
  if (task.status === 'canceled') {
    return {
      Icon: XCircle,
      iconBgColor: 'bg-gray-50',
      iconColor: 'text-gray-500',
      iconBorderColor: 'border-gray-300',
      tooltipText: 'Canceled',
    }
  }

  // Blocked task - on hold or blocked by external reasons
  if (task.is_blocked) {
    const blockReason = task.block_reason || 'Unknown reason'
    if (task.block_category === 'hold') {
      // On hold - use orange/warning colors matching badge
      return {
        Icon: Pause,
        iconBgColor: 'bg-orange-50',
        iconColor: 'text-orange-400',
        iconBorderColor: 'border-orange-300',
        tooltipText: `On Hold: ${blockReason}`,
      }
    } else {
      // Externally blocked - use gray/neutral colors matching badge
      return {
        Icon: X,
        iconBgColor: 'bg-gray-100',
        iconColor: 'text-gray-500',
        iconBorderColor: 'border-gray-400',
        tooltipText: `Blocked: ${blockReason}`,
      }
    }
  }

  // Ready to execute - automated or manual (yellow/caution matching badge)
  // This handles 'pending' and 'excluded' statuses
  const Icon = task.execution_method === 'automated' ? Bot : UserRound

  return {
    Icon,
    iconBgColor: 'bg-yellow-50',
    iconColor: 'text-yellow-400',
    iconBorderColor: 'border-yellow-300',
    tooltipText: 'Ready to execute',
  }
}

interface NextUpTaskItemProps {
  task: BrokersDependencyTaskSummary
}

/**
 * Single next up task display component
 * Renders a task with custom styled icon and black text
 */
const NextUpTaskItem: React.FC<NextUpTaskItemProps> = ({ task }) => {
  const { Icon, iconBgColor, iconColor, iconBorderColor, tooltipText } =
    getTaskDisplayMetadata(task)

  return (
    <div className='flex items-center gap-2'>
      <TooltipWrapper tooltipText={tooltipText}>
        <div
          className={`flex items-center justify-center rounded-md border p-1 ${iconBgColor} ${iconBorderColor}`}
        >
          <Icon className={`h-3 w-3 ${iconColor}`} />
        </div>
      </TooltipWrapper>
      <span className='truncate text-sm text-gray-900'>
        {task.display_name}
      </span>
    </div>
  )
}

interface NextUpTasksListProps {
  tasks: BrokersDependencyTaskSummary[]
  maxVisible?: number
  className?: string
}

/**
 * List of next up tasks with optional limit
 * Displays tasks with icons and shows "and X more" if there are additional tasks
 */
export const NextUpTasksList: React.FC<NextUpTasksListProps> = ({
  tasks,
  maxVisible = 3,
  className = 'flex flex-col gap-1 text-sm items-start',
}) => {
  if (!tasks || tasks.length === 0) {
    return <span className='text-gray-500'>-</span>
  }

  const displayTasks = tasks.slice(0, maxVisible)
  const remainingCount = tasks.length - displayTasks.length

  return (
    <div className={className}>
      {displayTasks.map((task) => (
        <NextUpTaskItem key={task.id} task={task} />
      ))}
      {remainingCount > 0 && (
        <span className='text-gray-500 text-xs ml-5'>
          and {remainingCount} more
        </span>
      )}
    </div>
  )
}

/**
 * Compact inline display for next up tasks
 * Renders task names as comma-separated text with icons
 */
export const NextUpTasksInline: React.FC<NextUpTasksListProps> = ({
  tasks,
  maxVisible = 3,
}) => {
  if (!tasks || tasks.length === 0) {
    return <span className='text-gray-500'>-</span>
  }

  const displayTasks = tasks.slice(0, maxVisible)
  const remainingCount = tasks.length - displayTasks.length
  const taskNames = displayTasks.map((task) => task.display_name).join(', ')

  return (
    <span className='text-sm'>
      {taskNames}
      {remainingCount > 0 && (
        <span className='text-gray-500 ml-1'>and {remainingCount} more</span>
      )}
    </span>
  )
}

// Export the helper function for use in other components if needed
export { getTaskDisplayMetadata }
